import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export const MAX_SO_PER_SPB = 5;

/**
 * useSoAllocations — encapsulates the multi-drop SO allocation flow used by
 * the SPB create form:
 *   • Fetches available SOs whenever plant/product/distrik change.
 *   • Auto-fills FIFO allocations from `jumlah` (until user starts editing).
 *   • Exposes update/remove/add controls + derived totals.
 *
 * Admins additionally receive over-quota SOs so they can knowingly ship past
 * the plan; the primary consumer flag is `hasOverQuotaAlloc` in the returned
 * derived state.
 */
export default function useSoAllocations({ plant_id, produk_id, distrik_id, jumlah, isAdmin }) {
  const [availableSOs, setAvailableSOs] = useState([]);
  const [soAllocations, setSoAllocations] = useState([]);
  const [manualAlloc, setManualAlloc] = useState(false);

  // Load available SOs
  useEffect(() => {
    if (!plant_id || !produk_id) {
      setAvailableSOs([]);
      setSoAllocations([]);
      return;
    }
    const params = { plant_id, produk_id };
    if (distrik_id) params.distrik_id = distrik_id;
    if (isAdmin) params.include_over_quota = true;
    api.get("/sales-orders/available", { params })
      .then(({ data }) => setAvailableSOs(data))
      .catch(() => setAvailableSOs([]));
  }, [plant_id, produk_id, distrik_id, isAdmin]);

  // Auto-fill FIFO whenever `jumlah` or availability changes.
  // Disabled once user manually edits allocations.
  useEffect(() => {
    if (manualAlloc) return;
    const total = parseFloat(jumlah);
    if (!total || total <= 0 || availableSOs.length === 0) {
      setSoAllocations([]);
      return;
    }
    let remaining = total;
    const allocs = [];
    // Pass 1: real stock (sisa > 0)
    for (const so of availableSOs) {
      if (remaining <= 0) break;
      if (allocs.length >= MAX_SO_PER_SPB) break;
      if (so.is_over_quota) continue;
      const take = Math.min(so.kuantiti_sisa, remaining);
      if (take > 0) {
        allocs.push({ so_id: so.id, no_so: so.no_so, qty: parseFloat(take.toFixed(3)), over_quota: false });
        remaining = parseFloat((remaining - take).toFixed(3));
      }
    }
    // Pass 2: dump the rest onto the first over-quota SO (admin-only visibility)
    if (remaining > 0.01 && allocs.length < MAX_SO_PER_SPB) {
      const overSo = availableSOs.find((s) => s.is_over_quota);
      if (overSo) {
        allocs.push({ so_id: overSo.id, no_so: overSo.no_so, qty: parseFloat(remaining.toFixed(3)), over_quota: true });
      }
    }
    setSoAllocations(allocs);
  }, [jumlah, availableSOs, manualAlloc]);

  const updateSoQty = (so_id, newQty) => {
    const q = parseFloat(newQty);
    setSoAllocations((prev) =>
      prev.map((a) => (a.so_id === so_id ? { ...a, qty: isNaN(q) ? 0 : parseFloat(q.toFixed(3)) } : a)),
    );
    setManualAlloc(true);
  };

  const removeSoRow = (so_id) => {
    setSoAllocations((prev) => prev.filter((a) => a.so_id !== so_id));
    setManualAlloc(true);
  };

  const addSoRow = (so_id) => {
    if (!so_id || soAllocations.some((a) => a.so_id === so_id)) return;
    if (soAllocations.length >= MAX_SO_PER_SPB) {
      toast.error(`Maksimal ${MAX_SO_PER_SPB} SO per SPB`);
      return;
    }
    const so = availableSOs.find((s) => s.id === so_id);
    if (!so) return;
    const jumlahN = parseFloat(jumlah) || 0;
    const usedNow = soAllocations.reduce((s, a) => s + Number(a.qty || 0), 0);
    const need = Math.max(0, jumlahN - usedNow);
    const suggested = Math.min(so.kuantiti_sisa || 0, need);
    setSoAllocations((prev) => [
      ...prev,
      {
        so_id: so.id,
        no_so: so.no_so,
        qty: parseFloat((suggested > 0 ? suggested : 0).toFixed(3)),
        over_quota: !!so.is_over_quota,
      },
    ]);
    setManualAlloc(true);
  };

  const resetAutoFill = () => setManualAlloc(false);

  const totalAllocated = useMemo(
    () => soAllocations.reduce((s, a) => s + Number(a.qty || 0), 0),
    [soAllocations],
  );
  const jumlahNum = parseFloat(jumlah) || 0;
  const soShortfall = Math.max(0, jumlahNum - totalAllocated);
  const hasOverQuotaAlloc = useMemo(
    () => soAllocations.some((a) => a.over_quota),
    [soAllocations],
  );
  const availableSoTotals = useMemo(() => {
    let healthy = 0;
    let overQuotaCount = 0;
    for (const x of availableSOs) {
      if (x.is_over_quota) overQuotaCount += 1;
      else healthy += Number(x.kuantiti_sisa || 0);
    }
    return { healthy, overQuotaCount };
  }, [availableSOs]);

  return {
    availableSOs,
    soAllocations,
    manualAlloc,
    updateSoQty,
    removeSoRow,
    addSoRow,
    resetAutoFill,
    totalAllocated,
    jumlahNum,
    soShortfall,
    hasOverQuotaAlloc,
    availableSoTotals,
  };
}
